import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useT } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/app/LanguageSwitcher";
import SEOHead from "../components/app/SEOHead";
import { toast } from "sonner";
import { GoogleLogo } from "@phosphor-icons/react";

export default function Register() {
  const { t } = useT();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error(t("consent.required"));
      return;
    }
    setSubmitting(true);
    try {
      await register(email, password, name, { marketing_opt_in: acceptedMarketing });
      // Analytics conversion event
      const { trackEvent } = await import("../components/app/AnalyticsPixels");
      trackEvent("sign_up", { method: "email" });
      if (window.fbq) window.fbq("track", "CompleteRegistration");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.register_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <SEOHead pageKey="register" />
      <div className="flex items-center justify-center p-8 sm:p-16">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
              <img src="/logo-full.png" alt="PMCoach AI" className="h-12 w-auto" data-testid="brand-logo" />
            </Link>
            <LanguageSwitcher />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-10">{t("auth.start_pipeline")}</h1>
          <p className="text-slate-600 mt-2">{t("auth.start_pipeline_sub")}</p>

          <button onClick={onGoogle} className="btn-secondary w-full mt-6 flex items-center justify-center gap-2" data-testid="google-signup-button">
            <GoogleLogo size={18} weight="bold" /> {t("common.cont_with_google")}
          </button>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400">
            <div className="flex-1 h-px bg-slate-200" /> {t("common.or")} <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={onSubmit} className="space-y-3" data-testid="register-form">
            <label className="overline">{t("common.name")}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name-input" />
            <label className="overline">{t("common.email")}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email-input" />
            <label className="overline">{t("common.password")}</label>
            <input type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="register-password-input" />

            <div className="mt-4 space-y-2">
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer" data-testid="consent-kvkk-label">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#002FA7]"
                  data-testid="consent-kvkk-checkbox"
                  required
                />
                <span className="leading-snug">
                  <Link to="/terms" target="_blank" className="text-[#002FA7] underline hover:no-underline">
                    {t("consent.terms")}
                  </Link>
                  {", "}
                  <Link to="/distance-sales" target="_blank" className="text-[#002FA7] underline hover:no-underline">
                    {t("consent.distance_sales")}
                  </Link>
                  {", "}
                  <Link to="/privacy" target="_blank" className="text-[#002FA7] underline hover:no-underline">
                    {t("consent.kvkk_privacy")}
                  </Link>{" "}
                  {t("auth.and")}{" "}
                  <Link to="/kvkk" target="_blank" className="text-[#002FA7] underline hover:no-underline">
                    {t("consent.kvkk_text")}
                  </Link>{" "}
                  {t("consent.kvkk_tail")}
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedMarketing}
                  onChange={(e) => setAcceptedMarketing(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#002FA7]"
                  data-testid="consent-marketing-checkbox"
                />
                <span>{t("consent.marketing")}</span>
              </label>
            </div>

            <button type="submit" disabled={submitting} className="btn-primary w-full mt-4" data-testid="register-submit-button">
              {submitting ? t("auth.creating") : t("auth.create_btn")}
            </button>
          </form>

          <div className="mt-5 text-sm text-slate-600">
            {t("auth.have_account")}{" "}
            <Link to="/login" className="text-[#002FA7] font-semibold hover:underline" data-testid="goto-login">{t("common.signin")}</Link>
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1758876201985-66c20343e681?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Studying"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
    </div>
  );
}
